import React, { useState, useEffect } from "react";
import { Link as ScrollLink } from "react-scroll";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useTheme } from "@/context/ThemeContext";
import { useBooking } from "@/context/BookingContext";
import logo from "../../images/logo.jpg";
import { trackEvent } from "@/utils/analytics";
import LocalizedLink, { useLocalizedPath, useActiveLocale } from "@/components/LocalizedLink";
import { SUPPORTED_LOCALES, withLocale, stripLocalePrefix } from "@/i18n/locales";
import "./Header.scss";

const scrollOptions = { duration: 500, smooth: true, offset: -80, spy: true };

const ArrowIcon = () => (
  <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden="true">
    <path d="M3 8h10M9 4l4 4-4 4" />
  </svg>
);

const SunIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
    <circle cx="12" cy="12" r="4" />
    <path d="M12 2v2M12 20v2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M2 12h2M20 12h2M4.9 19.1l1.4-1.4M17.7 6.3l1.4-1.4" />
  </svg>
);

const MoonIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
    <path d="M21 12.8A9 9 0 1 1 11.2 3a7 7 0 0 0 9.8 9.8z" />
  </svg>
);

const Header: React.FC = () => {
  const { t } = useTranslation();
  const { theme, toggleTheme } = useTheme();
  const { openBooking } = useBooking();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const localizedPath = useLocalizedPath();
  const currentLang = useActiveLocale();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  const strippedPath = stripLocalePrefix(pathname);
  const isLanding = strippedPath === "/" || strippedPath === "";

  const cycleLang = () => {
    const idx = SUPPORTED_LOCALES.indexOf(currentLang);
    const next = SUPPORTED_LOCALES[(idx + 1) % SUPPORTED_LOCALES.length];
    const target = withLocale(next, strippedPath) + (window.location.hash || "");
    navigate(target);
  };

  useEffect(() => {
    const h = () => setScrolled(window.scrollY > 24);
    h();
    window.addEventListener("scroll", h, { passive: true });
    return () => window.removeEventListener("scroll", h);
  }, []);

  useEffect(() => {
    document.body.style.overflow = mobileOpen ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [mobileOpen]);

  const navLinks = [
    { to: "capabilities-section", label: t("header.solutions") },
    { to: "ai-feature-section", label: t("header.ai"), dot: true },
    { to: "about-section", label: t("header.about") },
    { to: "portfolio-section", label: t("header.work") },
    { to: "packages-section", label: t("header.pricing") },
    { to: "contact-section", label: t("header.contact") },
  ];

  const insightsLabel = t("insights.label");

  const NavLink = ({ to, className, dot, onClick, children }: {
    to: string;
    className?: string;
    dot?: boolean;
    onClick?: () => void;
    children: React.ReactNode;
  }) => {
    if (isLanding) {
      return (
        <ScrollLink to={to} {...scrollOptions} activeClass="active" className={className} onClick={onClick}>
          {children}
          {dot && <span className="dot" aria-hidden="true" />}
        </ScrollLink>
      );
    }
    return (
      <a href={`${localizedPath("/")}#${to}`} className={className} onClick={onClick}>
        {children}
        {dot && <span className="dot" aria-hidden="true" />}
      </a>
    );
  };

  const HomeLink = ({ className, children }: { className?: string; children: React.ReactNode }) => {
    if (isLanding) {
      return (
        <ScrollLink to="hero-section" {...scrollOptions} className={className} aria-label={t("header.homeAriaLabel")}>
          {children}
        </ScrollLink>
      );
    }
    return (
      <LocalizedLink to="/" className={className} aria-label={t("header.homeAriaLabel")}>
        {children}
      </LocalizedLink>
    );
  };

  return (
    <>
      <header className={`nav${scrolled ? " scrolled" : ""}`}>
        <div className="container nav-row">
          <HomeLink className="logo">
            <img src={logo} alt="" className="logo-mark" />
            <span>Devbloom</span>
          </HomeLink>

          <nav className="nav-links" aria-label={t("header.mainNavAriaLabel")}>
            {navLinks.map((item) => (
              <NavLink key={item.to} to={item.to} className="nav-link" dot={item.dot}>
                {item.label}
              </NavLink>
            ))}
            <LocalizedLink to="/insights" className="nav-link">
              {insightsLabel}
            </LocalizedLink>
          </nav>

          <div className="nav-actions">
            <button className="icon-btn lang-switch" onClick={cycleLang} aria-label={t("header.changeLangAriaLabel")}>
              {currentLang.toUpperCase()}
            </button>
            <button className="icon-btn" onClick={toggleTheme} aria-label={theme === "dark" ? t("header.switchToLightAriaLabel") : t("header.switchToDarkAriaLabel")}>
              {theme === "dark" ? <SunIcon /> : <MoonIcon />}
            </button>
            <button
              className="nav-cta"
              onClick={() => { trackEvent("cta_click", { location: "header" }); openBooking("header"); }}
            >
              {t("booking.cta")} <ArrowIcon />
            </button>
            <button
              className={`hamburger${mobileOpen ? " open" : ""}`}
              onClick={() => setMobileOpen(!mobileOpen)}
              aria-label={t("header.toggleMenuAriaLabel")}
              aria-expanded={mobileOpen}
            >
              <span /><span /><span />
            </button>
          </div>
        </div>
      </header>

      <div className={`mobile-overlay${mobileOpen ? " open" : ""}`} aria-hidden={!mobileOpen}>
        <nav className="mobile-nav">
          {navLinks.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              className="mobile-link"
              onClick={() => setMobileOpen(false)}
            >
              {item.label}
            </NavLink>
          ))}
          <LocalizedLink
            to="/insights"
            className="mobile-link"
            onClick={() => setMobileOpen(false)}
          >
            {insightsLabel}
          </LocalizedLink>
          <button
            className="mobile-cta"
            onClick={() => { setMobileOpen(false); trackEvent("cta_click", { location: "header" }); openBooking("header"); }}
          >
            {t("booking.cta")}
          </button>
          <div className="mobile-controls">
            <button className="icon-btn lang-switch" onClick={cycleLang} aria-label={t("header.changeLangAriaLabel")}>
              {currentLang.toUpperCase()}
            </button>
            <button className="icon-btn" onClick={toggleTheme} aria-label={t("header.toggleThemeAriaLabel")}>
              {theme === "dark" ? <SunIcon /> : <MoonIcon />}
            </button>
          </div>
        </nav>
      </div>
    </>
  );
};

export default Header;
